import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRightIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  SparklesIcon,
  UserIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import api from "../services/api";
import { getRightLabel } from "../account/rightCatalog";
import {
  createOfferRightRows,
  isUnavailableOfferRightValue,
} from "./publicOffersRights";

const gradeMeta = {
  3: { shortName: "Utilisateur", subtitle: "Compte particulier gratuit", accent: "text-sky-200" },
  4: { shortName: "Premium", subtitle: "Particulier avec abonnement", accent: "text-violet-200" },
  5: { shortName: "Professionnel", subtitle: "Offre complète pour une organisation", accent: "text-emerald-200" },
  6: { shortName: "Membre professionnel", subtitle: "Extension incluse et rattachée à Professionnel", accent: "text-amber-200" },
};

const GRADE_ORDER = [3, 4, 5, 6];

const formatLimit = (rightName, value, gradeId) => {
  const parsedValue = Number(value);
  if (parsedValue < 0) return "Illimité";
  if (rightName === "TrialDurationDays") return `${parsedValue} jours`;
  if ([5, 6].includes(Number(gradeId)) && ["VehiculeMaxActive", "VehiculeMaxSold"].includes(rightName)) {
    return `${parsedValue} partagés`;
  }
  return parsedValue;
};

const UnavailableRightValue = () => (
  <span className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-500">
    <XCircleIcon className="h-5 w-5" />
    Non
  </span>
);

const RightValue = ({ row, grade }) => {
  const right = row.values[grade.GradeID];
  if (isUnavailableOfferRightValue(row, right)) return <UnavailableRightValue />;
  if (row.name === "PriceMonthlyCents") {
    const cents = Number(right.Valeur);
    if (Number(grade.GradeID) === 6) return <span className="font-semibold text-amber-200">Inclus avec Pro</span>;
    if (cents === 0) return <span className="font-semibold text-emerald-300">Gratuit</span>;
    return <span className="font-semibold text-white">{(cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €/mois</span>;
  }
  if (row.name === "AnnualDiscountPercent") {
    const percent = Number(right.Valeur);
    return <span className="font-semibold text-emerald-300">-{percent} %</span>;
  }
  if (row.name === "ActivityJournalScope") {
    return <span className="font-semibold text-slate-100">{({ 1: "Activité personnelle", 2: "Organisation" })[Number(right.Valeur)]}</span>;
  }
  if (row.name === "AttachmentMaxPerItem") {
    const count = Number(right.Valeur);
    return <span className="font-semibold text-slate-100">{count < 0 ? "Illimité" : `${count} fichier${count > 1 ? "s" : ""}`}</span>;
  }
  if (row.name === "AttachmentMaxFileMb") {
    return <span className="font-semibold text-slate-100">{Number(right.Valeur)} Mo par fichier</span>;
  }
  if (row.type === "LIMIT") {
    return <span className="font-semibold text-slate-100">{formatLimit(row.name, right.Valeur, grade.GradeID)}</span>;
  }
  return (
    <span className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-300">
      <CheckCircleIcon className="h-5 w-5" />
      Inclus
    </span>
  );
};

const PublicOffersPage = ({ authenticated = false }) => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const rows = useMemo(() => createOfferRightRows(grades), [grades]);

  useEffect(() => {
    const loadRights = async () => {
      try {
        const response = await api.get("/account/public-rights");
        setGrades([...(response.data.grades || [])].sort((left, right) => GRADE_ORDER.indexOf(left.GradeID) - GRADE_ORDER.indexOf(right.GradeID)));
      } catch (err) {
        setError(err.response?.data?.error || "Impossible de charger les offres actuellement.");
      } finally {
        setLoading(false);
      }
    };
    loadRights();
  }, []);

  return (
    <main className={`${authenticated ? "" : "min-h-screen"} text-slate-100`}>
      {!authenticated && <header className="border-b border-white/10 bg-slate-950/70 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/offres" className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-sky-500/15 text-sky-300">
              <SparklesIcon className="h-5 w-5" />
            </span>
            <div>
              <p className="font-semibold text-white">Vehicle</p>
              <p className="text-xs text-slate-500">Offres et droits</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="rounded-md px-3 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5 hover:text-white">Connexion</Link>
            <Link to="/register?type=utilisateur" className="rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-500">Créer un compte</Link>
          </div>
        </div>
      </header>}

      <div className={`mx-auto max-w-7xl ${authenticated ? "py-4" : "px-4 py-10 sm:px-6 lg:px-8 lg:py-16"}`}>
        <section className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-300">Choisir son espace</p>
          <h1 className="mt-4 text-3xl font-semibold text-white sm:text-5xl">Des droits adaptés à chaque usage</h1>
          <p className="mx-auto mt-5 max-w-3xl text-sm leading-7 text-slate-300 sm:text-base">
            Comparez les limites et permissions réellement configurées dans Vehicle. Les changements effectués par l'administration sont automatiquement reflétés ici.
          </p>
        </section>

        <section className="mt-10 overflow-hidden rounded-xl border border-sky-500/20 bg-slate-950/70 shadow-[0_24px_80px_rgba(2,6,23,0.45)]">
          <div className="border-b border-white/10 px-5 py-4 sm:px-6">
            <h2 className="text-xl font-semibold text-white">Tableau des droits</h2>
            <p className="mt-1 text-sm text-slate-400">Les quotas professionnels sont partagés entre le compte principal et ses membres.</p>
            <div className="mt-4 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-xs leading-5 text-emerald-100">
              <strong>Membre professionnel</strong> n’est pas une offre autonome : c’est une extension incluse dans l’espace Professionnel, créée et administrée par le compte principal.
            </div>
          </div>
          {loading ? (
            <p className="p-6 text-sm text-amber-200">Chargement du moteur des droits...</p>
          ) : error ? (
            <p className="m-6 rounded-md border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{error}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[900px] w-full divide-y divide-white/10 text-sm">
                <thead className="bg-slate-900/90">
                  <tr>
                    <th className="sticky left-0 z-10 min-w-64 bg-slate-900 px-4 py-4 text-left font-semibold text-slate-200">Fonctionnalité</th>
                    {grades.map((grade) => (
                      <th key={grade.GradeID} className={`min-w-40 px-3 py-4 text-center ${Number(grade.GradeID) === 6 ? "border-l-2 border-emerald-500/40 bg-emerald-500/5" : ""}`}>
                        <span className={`block font-semibold ${gradeMeta[grade.GradeID]?.accent || "text-white"}`}>{gradeMeta[grade.GradeID]?.shortName || grade.Nom}</span>
                        <span className="mt-1 block text-[11px] font-normal text-slate-500">{gradeMeta[grade.GradeID]?.subtitle}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {rows.map((row) => (
                    <tr key={row.name} className="hover:bg-white/[0.03]">
                      <th className="sticky left-0 z-10 bg-slate-950 px-4 py-3 text-left font-medium text-slate-200">{getRightLabel(row.name)}</th>
                      {grades.map((grade) => (
                        <td key={grade.GradeID} className={`px-3 py-3 text-center ${Number(grade.GradeID) === 6 ? "border-l-2 border-emerald-500/20 bg-emerald-500/[0.025]" : ""}`}><RightValue row={row} grade={grade} /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {!authenticated ? <section className="mt-10 grid gap-6 lg:grid-cols-2">
          <article className="rounded-xl border border-sky-500/20 bg-slate-950/70 p-6">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-sky-500/15 text-sky-300"><UserIcon className="h-5 w-5" /></span>
            <h2 className="mt-4 text-xl font-semibold text-white">Je suis un particulier</h2>
            <p className="mt-2 text-sm leading-6 text-slate-400">Commencez avec le compte Utilisateur puis activez Premium depuis l'espace abonnement si vous souhaitez augmenter vos limites.</p>
            <Link to="/register?type=utilisateur" className="mt-5 inline-flex items-center gap-2 rounded-md bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-500">
              Créer un compte particulier <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </article>
          <article className="rounded-xl border border-emerald-500/20 bg-slate-950/70 p-6">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-emerald-500/15 text-emerald-300"><BuildingOffice2Icon className="h-5 w-5" /></span>
            <h2 className="mt-4 text-xl font-semibold text-white">Je suis un professionnel</h2>
            <p className="mt-2 text-sm leading-6 text-slate-400">Créez l'espace professionnel principal. Les comptes supplémentaires seront ensuite invités et gérés directement par ce compte.</p>
            <Link to="/register?type=professionnel" className="mt-5 inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500">
              Créer un compte professionnel <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </article>
        </section> : (
          <section className="mt-10 rounded-xl border border-sky-500/20 bg-slate-950/70 p-6 text-center">
            <h2 className="text-xl font-semibold text-white">Votre compte est déjà connecté</h2>
            <p className="mt-2 text-sm text-slate-400">Consultez ici les offres, puis gérez votre abonnement et vos simulations de paiement depuis votre espace.</p>
            <Link to="/abonnement" className="mt-5 inline-flex items-center gap-2 rounded-md bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-500">Gérer mon abonnement <ArrowRightIcon className="size-4" /></Link>
          </section>
        )}

        <p className="mt-8 text-center text-xs leading-5 text-slate-500">Les abonnements et paiements affichés dans la version actuelle sont entièrement factices et ne déclenchent aucun débit réel.</p>
      </div>
    </main>
  );
};

export default PublicOffersPage;
